import React from "react"
import { graphql, Link, useStaticQuery } from "gatsby"
import tw, { styled } from "twin.macro"
import moment from "moment"

import HubspotForm from "react-hubspot-form"

import LogoMark from "images/logo-mark-footer.inline.svg"
import IconYoutube from "images/icons/youtube.inline.svg"
import IconLinkedin from "images/icons/linkedin.inline.svg"
import IconInstagram from "images/icons/instagram.inline.svg"
import IconTwitter from "images/icons/twitter.inline.svg"
import MailIcon from "images/icons/email-black.svg"
import LinkTo from "components/link-to"
import PressMoreInfo from "components/press-more-info"

import { brandRules } from "utils/brand"

const MenuTitle = styled.div`
  ${tw`py-2 font-display uppercase text-center m:text-left`}
  font-size: 15px;
  line-height: 14px;
  color: #f22e52;
`

const MenuItem = styled.div`
  ${tw`mb-1`}

  &:hover {
    text-decoration: underline;
  }
`

const Hubspot = styled.div`
  ${tw`overflow-hidden`}

  .hs-form {
    ${tw`text-black`}
    font-size: 12px;

    legend,
    label {
      margin: 0;
    }
  }

  .hs-field-desc,
  .hs-form label {
    ${tw`text-black`}

    &.hs-error-msg {
      ${tw`text-neonred`}
    }
  }

  .stacked {
    margin: 0;
  }

  .hs-email label {
    ${tw`hidden h-0 m-0 p-0`}

    &.hs-error-msg {
      ${tw`block h-auto`}
    }
  }

  .hs-form .field {
    padding: 0;
    padding-top: 5px;
  }

  .hs-button {
    ${tw`bg-yellow rounded p-3 px-4 uppercase font-display text-black text-sm flex-shrink block`}
    background: linear-gradient(180deg, #F7BC2D 0%, #C69724 100%);
    box-shadow: none;
    border: none;
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
    text-shadow: none;
    height: auto;
    width: auto;
  }

  .hs-email .input {
    position: relative;

    &:after {
      content: "";
      background-image: url(${MailIcon});
      background-repeat: no-repeat;
      display: block;
      position: absolute;
      top: 11px;
      left: 12px;
      height: 20px;
      width: 20px;
    }
  }

  .hs-email .hs-input {
    ${tw`bg-grey-95`}
    border-radius: 5px;
    box-shadow: 0 2px 7px 0 #e6e6e6;
    border: solid 1px #ffffff;
    padding-left: 40px;
    height: 44px;
    width: 100%;

    &:focus {
      background-color: #ffffff;
    }
  }

  .actions {
    ${tw`flex flex-col items-center m:items-start`}
  }
`

const Footer = styled.div`
  ${tw`font-body text-center m:text-left text-grey-5`}
  font-size: 12px;
  line-height: 26px;
  position: relative;

  &::before {
    ${tw`bg-grey-95`}
    content: "";
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: calc(100% + 100px);
    clip-path: polygon(0% 10px, 100% 0%, 100% 100%, 0% 100%);
    @media (min-width: 640px) {
      clip-path: polygon(0% 45px, 100% 0%, 100% 100%, 0% 100%);
    }
  }

  div {
    position: relative;
    z-index: 1;
  }
`

const FooterWrapper = styled.div`
  filter: drop-shadow(0 -6px 10px rgba(255, 255, 255, 0.25));
`

const MenuItemLink = ({ linkInfo, title }) => {
  return (
    <LinkTo linkInfo={linkInfo}>
      <MenuItem dangerouslySetInnerHTML={{ __html: brandRules(title) }} />
    </LinkTo>
  )
}

const MenuItems = ({ menuItems }) => (
  <>
    {menuItems.map((menuItem) => (
      <MenuItemLink
        linkInfo={menuItem}
        title={menuItem.title}
        key={menuItem.id}
      />
    ))}
  </>
)

export default () => {
  const data = useStaticQuery(graphql`
    query {
      gcms {
        footerMenus: menus(
          orderBy: order_ASC
          where: { menuLocation: Footer }
        ) {
          id
          title
          url
          isExternal
          order
          menuItems {
            id
            title
            url
            isExternal
            openInNewTab
          }
        }
      }
    }
  `)

  const { footerMenus } = data.gcms

  return (
    <FooterWrapper>
      <Footer className="bg-grey-95 py-4">
        <div className="px-2 flex flex-wrap items-center justify-center m:items-start m:justify-start overflow-hidden mx-auto w-11/12 m:max-w-m l:max-w-l">
          {footerMenus.map((menu) => (
            <div className="w-full m:w-1/5" key={menu.id}>
              <LinkTo linkInfo={menu}>
                <MenuTitle>{menu.title}</MenuTitle>
              </LinkTo>
              <MenuItems menuItems={menu.menuItems} />
              <hr className="light my-2 m:hidden w-2/3" />
            </div>
          ))}

          <div className="w-full m:w-1/5 flex flex-col justify-center m:justify-start">
            <MenuTitle>Insights in your inbox</MenuTitle>
            <Hubspot>
              <HubspotForm
                portalId={process.env.GATSBY_HUBSPOT_ID}
                formId={process.env.GATSBY_HUBSPOT_NEWSLETTER_FORM}
                loading={<div>Loading...</div>}
                formInstanceId="footer-newsletter-form"
              />
            </Hubspot>
          </div>
          <hr className="light my-2 m:hidden" />
          <PressMoreInfo className="py-5 m:pt-8 m:pb-2 text-grey-5 footer flex" />
          <hr className="light my-2 m:hidden" />
        </div>
        <div className="px-2 hidden m:block mx-auto max-w-s m:max-w-m l:max-w-l">
          <hr className="light my-2" />
        </div>
        <div className="px-2 flex flex-col m:flex-row-reverse mx-auto w-11/12 m:max-w-m l:max-w-l">
          <div
            id="social-icons"
            className="py-4 m:py-2 m:w-1/3 flex flex-row items-center justify-center m:justify-end opacity-50"
          >
            <a href="https://www.youtube.com/channel/UCAAJjdGWv_Xm5OK7wAzhxmg">
              <IconYoutube className="flex-shrink mr-1 m:mr-1" />
            </a>
            <a href="https://www.linkedin.com/company/parrot-analytics/">
              <IconLinkedin className="flex-shrink mx-1 m:mx-1" />
            </a>
            <a href="https://www.instagram.com/globaltvdemandawards/">
              <IconInstagram className="flex-shrink mx-1 m:mx-1" />
            </a>
            <a href="https://twitter.com/ParrotAnalytics">
              <IconTwitter className="flex-shrink mx-1 m:mx-1" />
            </a>
          </div>
          <div className="py-2 m:py-3 m:w-1/3 text-center font-body text-grey-60 text-xs hidden m:flex items-center justify-center">
            <Link to="/terms-of-service">Terms of Service</Link>&nbsp;|&nbsp;
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
          <div className="py-2 m:py-3 m:w-1/3 text-center m:text-left font-body text-grey-60 text-xs flex items-center">
            © 2011–{moment().format("YYYY")} Parrot Analytics Limited. All
            rights reserved.
          </div>
          <div className="py-4 flex-1 text-center font-body text-grey-60 text-xs m:hidden">
            <Link to="/terms-of-service">Terms of Service</Link>&nbsp;|&nbsp;
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </div>
      </Footer>
    </FooterWrapper>
  )
}
